import type { EffectGroup, EffectGroupOption } from '../hooks/useTemplates';

export interface ResolvedEffectSelection {
  group: EffectGroup;
  option: EffectGroupOption;
}

/**
 * Infers a canonical market code from an option id or value by reading the suffix.
 * E.g. "dream-cat-kr" → "kr", "dream-cat-en" → "global".
 * Returns null if no recognisable suffix is found.
 */
function inferMarketFromOptionId(idOrValue: string): string | null {
  const suffix = idOrValue.split('-').pop()?.toLowerCase();
  if (!suffix) return null;
  // "en" is treated as global for matching purposes
  if (suffix === 'en') return 'global';
  return suffix;
}

/**
 * Returns true when `candidate` matches `normalizedMarket` via explicit metadata or
 * backward-compatible id/value suffix inference.
 */
function optionMatchesMarket(candidate: EffectGroupOption, normalizedMarket: string): boolean {
  // Explicit metadata takes priority.
  if (candidate.market !== undefined) {
    if (candidate.market.toLowerCase() === normalizedMarket) return true;
    if (candidate.marketAliases?.some((a) => a.toLowerCase() === normalizedMarket)) return true;
    // Also match "en" against "global" explicitly.
    if (normalizedMarket === 'en' && (
      candidate.market.toLowerCase() === 'global' ||
      candidate.marketAliases?.some((a) => a.toLowerCase() === 'global')
    )) return true;
    // Don't fall through to inference when explicit metadata is present — be strict.
    return false;
  }
  // Backward-compatible inference from id / value.
  const inferred = inferMarketFromOptionId(candidate.id) ?? inferMarketFromOptionId(candidate.value);
  if (!inferred) return false;
  if (inferred === normalizedMarket) return true;
  // "global" inferred matches both "global" and "en" query.
  if (inferred === 'global' && normalizedMarket === 'en') return true;
  return false;
}

/**
 * Finds the best-matching option for a given market within an effect group.
 *
 * Resolution order:
 *  1. Exact market match (via explicit metadata or suffix inference).
 *  2. Global/EN fallback option.
 *  3. First option in the group.
 */
export function getMarketDefaultOptionForGroup(group: EffectGroup, market: string): EffectGroupOption | null {
  if (!group.options || group.options.length === 0) return null;
  const normalizedMarket = market.toLowerCase();

  // 1. Exact market match.
  const exactMatch = group.options.find((opt) => optionMatchesMarket(opt, normalizedMarket));
  if (exactMatch) return exactMatch;

  // 2. Global/EN fallback.
  const globalMatch = group.options.find((opt) => optionMatchesMarket(opt, 'global') || optionMatchesMarket(opt, 'en'));
  if (globalMatch) return globalMatch;

  // 3. First option.
  return group.options[0] ?? null;
}

/**
 * Returns the default effect selection for a set of effectGroups.
 * When `market` is provided the first group's option is chosen to match the market.
 */
export function getDefaultEffectSelection(effectGroups?: EffectGroup[], market?: string): ResolvedEffectSelection | null {
  if (!effectGroups || effectGroups.length === 0) return null;
  for (const group of effectGroups) {
    if (!group.options || group.options.length === 0) continue;
    const option = market
      ? (getMarketDefaultOptionForGroup(group, market) ?? group.options[0])
      : group.options[0];
    return { group, option };
  }
  return null;
}

export function getEffectSelectionByOptionId(
  effectGroups: EffectGroup[] | undefined,
  optionId: string | null | undefined,
): ResolvedEffectSelection | null {
  if (!effectGroups || !optionId) return null;
  for (const group of effectGroups) {
    const option = group.options.find((candidate) => candidate.id === optionId);
    if (option) {
      return { group, option };
    }
  }
  return null;
}

export function getResolvedEffectSelection(
  effectGroups: EffectGroup[] | undefined,
  optionId: string | null | undefined,
  market?: string,
): ResolvedEffectSelection | null {
  return getEffectSelectionByOptionId(effectGroups, optionId) ?? getDefaultEffectSelection(effectGroups, market);
}
